import { useState } from "react";
import { CalendarClock, Building2, Share2, Plus, Minus, ArrowUpRight, ScanLine, Ruler } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { scrollToId } from "@/components/landing/Navbar";

const SERVICES = [
  {
    id: "drohnen-inspektion",
    icon: ScanLine,
    index: "03",
    title: "Drohnen-Inspektion",
    subtitle: "Dach · Fassade · Industrie",
    text: "Professionelle Drohnen-Inspektion von Dächern, Fassaden, Industrieanlagen, Schornsteinen und Windkraftanlagen. Wir erkennen Schäden aus nächster Nähe — ohne Gerüst, ohne Höhenrisiko — und liefern jeden Befund als nachvollziehbaren Einsatzbericht inklusive hochauflösender Aufnahmen und optionaler Thermografie.",
  },
  {
    id: "rtk-vermessung",
    icon: Ruler,
    index: "04",
    title: "RTK-Vermessung",
    subtitle: "Zentimetergenau aus der Luft",
    text: "Präzise RTK-Vermessung und Luftbild-Vermessung mit zentimetergenauer Georeferenzierung. Ob Volumenberechnung, Flächenaufmaß, Orthofotos oder 3D-Modelle — unsere RTK-Drohnen liefern vermessungsgenaue Daten für Bau, Planung und Dokumentation.",
  },
  {
    id: "eventbegleitung",
    icon: CalendarClock,
    index: "05",
    title: "Eventbegleitung",
    subtitle: "Veranstaltungen aus der Luft",
    text: "Luftaufnahmen, Übersicht und Lagebild für Veranstaltungen — vom Überblick über das Gelände bis zur professionellen Dokumentation des Ablaufs.",
  },
  {
    id: "hausverwaltung",
    icon: Building2,
    index: "06",
    title: "Hausverwaltung",
    subtitle: "Immobilien im Blick",
    text: "Regelmäßige Zustandskontrolle von Dächern, Fassaden und Außenanlagen — Schäden früh erkennen, bevor sie teuer werden. Ohne Gerüst, ohne Aufwand.",
  },
  {
    id: "weitergabe",
    icon: Share2,
    index: "07",
    title: "Dokumentation & Weitergabe",
    subtitle: "Transparent an Fachfirmen",
    text: "Aufbereitete Ergebnisse gehen direkt und nachvollziehbar an Ihre Fachfirmen und Gewerke — jeder Schritt transparent, jede Entscheidung belegbar.",
  },
];

export default function Services() {
  const [open, setOpen] = useState("drohnen-inspektion");

  return (
    <section id="weitere-leistungen" data-testid="services-section" className="relative">
      <div className="border-b border-white/10 px-6 py-20 lg:px-16 lg:py-28">
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">DROHNEN-INSPEKTION · RTK-VERMESSUNG · MEHR</p>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-7xl">
            Unsere <span className="text-stroke">Leistungen.</span>
          </h2>
          <p className="mt-6 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
            Von der Drohnen-Inspektion über die zentimetergenaue RTK-Vermessung bis zur lückenlosen
            Einsatzdokumentation — alle Leistungen aus einer Hand, zertifiziert nach EU 2019/947.
          </p>
        </Reveal>
      </div>

      <div className="border-b border-white/10">
        {SERVICES.map((service, i) => {
          const Icon = service.icon;
          const isOpen = open === service.id;
          return (
            <Reveal key={service.id} delay={i * 0.08}>
              <div className="border-b border-white/10 last:border-b-0" data-testid={`service-card-${service.id}`}>
                <button
                  data-testid={`service-toggle-${service.id}`}
                  onClick={() => setOpen(isOpen ? null : service.id)}
                  className="group flex w-full items-center gap-5 px-6 py-7 text-left lg:gap-8 lg:px-16 lg:py-9"
                >
                  <span aria-hidden="true" className="font-display text-2xl font-black leading-none text-stroke opacity-40 lg:text-4xl">
                    {service.index}
                  </span>
                  <Icon className={`h-7 w-7 shrink-0 transition-transform duration-500 lg:h-9 lg:w-9 ${isOpen ? "text-[#F5B301] scale-110" : "text-[#F5B301]/70"}`} strokeWidth={1.5} />
                  <span className="flex-1">
                    <span className={`block font-display text-2xl font-black uppercase tracking-tighter transition-colors duration-300 lg:text-4xl ${isOpen ? "text-[#F5B301]" : "text-white group-hover:text-[#F5B301]"}`}>
                      {service.title}
                    </span>
                    <span className="mt-1 block font-tac text-[10px] uppercase tracking-[0.3em] text-[#F5B301]/80">
                      {service.subtitle}
                    </span>
                  </span>
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center border transition-colors duration-300 ${isOpen ? "border-[#F5B301] text-[#F5B301]" : "border-white/25 text-white/60 group-hover:border-[#F5B301]"}`}>
                    {isOpen ? <Minus className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                  </span>
                </button>
                <div className={`grid overflow-hidden transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="px-6 pb-9 lg:px-16 lg:pl-[8.5rem]">
                      <p className="max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">{service.text}</p>
                      <button
                        data-testid={`service-cta-${service.id}`}
                        onClick={() => scrollToId("#kontakt")}
                        className="mt-6 inline-flex items-center gap-2 font-tac text-[11px] tracking-[0.25em] text-white/80 transition-colors hover:text-[#F5B301]"
                      >
                        ANFRAGE STARTEN <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
